// Global Instructions Rule Applied!
//
// The React wrapper over fundOptions.ts. Reads globalConfigs from the store —
// never a fetch, per CLAUDE.md — and memoises the derived list.
//
// showAll is COMPONENT-LOCAL and deliberately not persisted. A sticky "show
// all" would silently defeat the whole feature for whoever ticked it once, and
// they would have no way of knowing why their picker is long again.

import { useMemo, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '../../../state_manager/store.ts'
import type { FundsModel } from '../../../state_manager/data_models/funds_model.ts'
import type { FundStatusModel } from '../../../state_manager/data_models/fund_status_model.ts'
import {
  selectFundOptions,
  isFundActive,
  type FundOptionRow,
  type FundOptionsGate
} from '../fundOptions.ts'

export interface UseFundOptionsResult {
  /** {value,label} rows — drop straight into any Select. */
  options: FundOptionRow[]
  /** The same funds in the same order, raw — for callers that need
   *  pipeline_origination or functional_currency_id off the row. */
  rows: FundsModel[]
  showAll: boolean
  setShowAll: (v: boolean) => void
  /** 0 → the caller hides its "show all" affordance entirely. */
  hiddenCount: number
  /** Is this fund offered by default under the caller's scope? */
  isActive: (fundId: string | null | undefined) => boolean
}

export function useFundOptions(gate: FundOptionsGate = {}): UseFundOptionsResult {
  const funds = useSelector(
    (state: RootState) => (state.globalConfigs?.funds || []) as FundsModel[]
  )
  const statuses = useSelector(
    (state: RootState) => (state.globalConfigs?.fund_statuses || []) as FundStatusModel[]
  )
  const [showAll, setShowAll] = useState(false)

  const { scope, pin, includeAll, onlyManagedByUs } = gate
  // A stable key so a caller passing an inline array does not re-memo forever.
  const pinKey = Array.isArray(pin) ? pin.join(',') : pin || ''

  const { options, hiddenCount } = useMemo(
    () => selectFundOptions(funds, statuses, { scope, pin, includeAll, onlyManagedByUs, showAll }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [funds, statuses, scope, pinKey, includeAll, onlyManagedByUs, showAll]
  )

  const rows = useMemo(() => options.map((o) => o.row), [options])

  const byId = useMemo(() => new Map(statuses.map((s) => [s.id, s])), [statuses])
  const isActive = useCallback(
    (fundId: string | null | undefined) => {
      if (!fundId) return true
      const f = funds.find((x) => x.id === fundId)
      return f ? isFundActive(f, byId, scope) : true
    },
    [funds, byId, scope]
  )

  return { options, rows, showAll, setShowAll, hiddenCount, isActive }
}
